import { useCallback, useEffect, useRef, useState } from 'react';
import { getEncryptedFilesByLocalIds, EncryptedFileRecord } from '../../../services/consents';
import {
    importKekFromBase64,
    decryptFileFromS3,
    DecryptFailureStage,
} from '../../../hooks/useDecryptedFiles';
import { decodeClinicalImageToBlob } from '../../../utils/clinicalImageDecode';
import { ImageBytes } from './imageMeta';

type DecryptTarget = { encryptedFileUuid: string; localId: string };

/**
 * SKIN-262 — decrypt each QC image independently so the gallery can flip card-by-card.
 *
 * Do NOT use `useDecryptedFiles` here: that hook publishes one array after the whole batch
 * finishes, so every card would stay on "loading" together (ReviewGallery's docblock).
 */
export function usePerImageDecrypt(
    uuidInvestigation: string | undefined,
    targets: DecryptTarget[],
): {
    bytesByUuid: Record<string, ImageBytes>;
    retry: (encryptedFileUuid: string) => void;
} {
    const [bytesByUuid, setBytesByUuid] = useState<Record<string, ImageBytes>>({});
    const blobUrlsRef = useRef<string[]>([]);
    const filesByLocalIdRef = useRef<Map<string, EncryptedFileRecord>>(new Map());
    const targetsKey = targets.map((t) => t.encryptedFileUuid).join(',');

    const setOne = useCallback((encryptedFileUuid: string, bytes: ImageBytes) => {
        setBytesByUuid((prev) => ({ ...prev, [encryptedFileUuid]: bytes }));
    }, []);

    const decryptOne = useCallback(async (
        uuidInvestigation: string,
        target: DecryptTarget,
        file: EncryptedFileRecord,
        kek: CryptoKey,
    ) => {
        setOne(target.encryptedFileUuid, { state: 'loading' });
        let stage: DecryptFailureStage = 'retrieve';
        try {
            const decrypted = await decryptFileFromS3(
                uuidInvestigation,
                file.s3Key,
                file.nonce,
                kek,
            );
            stage = 'decode';
            const blob = await decodeClinicalImageToBlob(
                new Uint8Array(decrypted),
                { format: file.format },
            );
            const blobUrl = URL.createObjectURL(blob);
            blobUrlsRef.current.push(blobUrl);
            setOne(target.encryptedFileUuid, { state: 'ready', blobUrl });
        } catch (err: any) {
            setOne(target.encryptedFileUuid, {
                state: 'failed',
                failedAt: stage,
                error: err?.message
                    || (stage === 'decode' ? 'Failed to decode image' : 'Failed to retrieve image'),
            });
        }
    }, [setOne]);

    useEffect(() => {
        if (!uuidInvestigation || targets.length === 0) return;
        let cancelled = false;

        (async () => {
            const kekBase64 = localStorage.getItem(`kek_${uuidInvestigation}_images`);
            if (!kekBase64) {
                for (const target of targets) {
                    setOne(target.encryptedFileUuid, {
                        state: 'failed',
                        failedAt: 'retrieve',
                        error: 'Missing images encryption key',
                    });
                }
                return;
            }

            // Seed loading states immediately so cards do not flash empty.
            for (const target of targets) {
                setOne(target.encryptedFileUuid, { state: 'loading' });
            }

            try {
                const localIds = Array.from(new Set(targets.map((t) => t.localId)));
                const listed = await getEncryptedFilesByLocalIds(uuidInvestigation, localIds);
                if (cancelled) return;
                const map = new Map<string, EncryptedFileRecord>();
                for (const file of listed.files ?? []) {
                    map.set(file.localId, file);
                }
                filesByLocalIdRef.current = map;

                const kek = await importKekFromBase64(kekBase64);
                // Fire per image without awaiting the batch — each card settles on its own.
                for (const target of targets) {
                    const file = map.get(target.localId);
                    if (!file) {
                        setOne(target.encryptedFileUuid, {
                            state: 'failed',
                            failedAt: 'retrieve',
                            error: 'Encrypted file not found',
                        });
                        continue;
                    }
                    void decryptOne(uuidInvestigation, target, file, kek);
                }
            } catch (err: any) {
                if (cancelled) return;
                for (const target of targets) {
                    setOne(target.encryptedFileUuid, {
                        state: 'failed',
                        failedAt: 'retrieve',
                        error: err?.message || 'Failed to list encrypted files',
                    });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // targetsKey stands in for the targets array identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuidInvestigation, targetsKey, decryptOne, setOne]);

    useEffect(() => () => {
        for (const url of blobUrlsRef.current) URL.revokeObjectURL(url);
        blobUrlsRef.current = [];
    }, []);

    const retry = useCallback((encryptedFileUuid: string) => {
        if (!uuidInvestigation) return;
        const target = targets.find((t) => t.encryptedFileUuid === encryptedFileUuid);
        if (!target) return;
        const file = filesByLocalIdRef.current.get(target.localId);
        const kekBase64 = localStorage.getItem(`kek_${uuidInvestigation}_images`);
        if (!file || !kekBase64) {
            setOne(encryptedFileUuid, {
                state: 'failed',
                failedAt: 'retrieve',
                error: !kekBase64 ? 'Missing images encryption key' : 'Encrypted file not found',
            });
            return;
        }
        void importKekFromBase64(kekBase64).then((kek) =>
            decryptOne(uuidInvestigation, target, file, kek),
        );
    }, [uuidInvestigation, targets, decryptOne, setOne]);

    return { bytesByUuid, retry };
}
